import React, { useState } from "react";
import moment from "moment";
import { ActivityLogProps } from "../../types/activityLog";
import ActivityLogDetailModal from "./ActivityLogDetailModal";

type Props = {
  logs: ActivityLogProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
};

const modelNameMap: Record<string, string> = {
  Account: "એકાઉન્ટ",
  AccountType: "એકાઉન્ટ ટાઈપ",
  Attendance: "હાજરી",
  Employees: "કામદાર",
  Items: "આઈટમ",
  Jobworks: "બાંધવા/ખોલવા",
  JobworksOrderParties: "બાંધવા/ખોલવા ઓર્ડર પાર્ટી",
  JobworksParties: "બાંધવા/ખોલવા પાર્ટી",
  JobworksPartiesItems: "બાંધવા/ખોલવા પાર્ટી આઈટમ",
  JobworksPayments: "બાંધવા/ખોલવા પેમેન્ટ",
  Orders: "ઓર્ડર",
  Parties: "પાર્ટી",
  PartiesItems: "પાર્ટી આઈટમ",
  PaymentHistory: "પેમેન્ટ હિસ્ટ્રી",
  PaymentNote: "પેમેન્ટ નોટ",
  Payments: "પેમેન્ટ",
  Processes: "પ્રોસેસ",
  Purchase: "ખરીદી",
  PurchaseDetail: "ખરીદી ડિટેઈલ",
  PurchaseItems: "ખરીદી આઈટમ",
  PurchaseParties: "ખરીદી પાર્ટી",
  PurchasePayments: "ખરીદી પેમેન્ટ",
  Salaries: "પગાર",
  SampleOrders: "સેમ્પલ ઓર્ડર",
  SamplePayments: "સેમ્પલ પેમેન્ટ",
  Setting: "સેટિંગ",
  Units: "યુનિટ",
  User: "યુઝર",
};

const ActivityLogList = ({ logs, pageNumber, pageSize, isLoading }: Props) => {
  const [selectedLog, setSelectedLog] = useState<ActivityLogProps | null>(null);

  return (
    <>
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                    Sr. No.
                  </th>
                  <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                    યુઝર
                  </th>
                  <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                    એકશન
                  </th>
                  <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                    મોડ્યુલ
                  </th>
                  <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                    તારીખ
                  </th>
                  <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                    વિગતો
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-primary-2 font-bold text-xl text-center py-2"
                    >
                      રાહ જુઓ...
                    </td>
                  </tr>
                ) : (
                  <>
                    {logs.length > 0 ? (
                      <>
                        {logs.map((log, index) => (
                          <tr key={log.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(pageNumber - 1) * pageSize + index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {log.user?.name || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-bold text-white ${
                                  log.action === "update"
                                    ? "bg-blue-500"
                                    : "bg-red-500"
                                }`}
                              >
                                {log.action === "update"
                                  ? "અપડેટ"
                                  : "ડિલીટ"}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {modelNameMap[log.model_name] || log.model_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {log.model_id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {moment(log.created_at).format(
                                "DD-MM-YYYY hh:mm A"
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={() => setSelectedLog(log)}
                                className="text-indigo-600 hover:text-indigo-900 font-medium"
                              >
                                જુઓ
                              </button>
                            </td>
                          </tr>
                        ))}
                      </>
                    ) : (
                      <tr>
                        <td
                          colSpan={7}
                          className="text-primary-2 font-bold text-xl text-center py-2"
                        >
                          એક્ટિવિટી લોગ ની વિગતો મળી નથી
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedLog && (
        <ActivityLogDetailModal
          log={selectedLog}
          modelNameMap={modelNameMap}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </>
  );
};
export default ActivityLogList;
